import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "@/lib/scrollToSection";

const links = [
  { id: "features", label: "Features" },
  { id: "how-it-works", label: "How it works" },
  { id: "preview", label: "Preview" },
  { id: "why-relyn", label: "Story" },
  { id: "faq", label: "FAQ" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while mobile menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const goto = (id, opts) => {
    setMenuOpen(false);
    // Give the menu-close animation a beat before scrolling.
    setTimeout(() => scrollToSection(id, opts), 60);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="nav-root"
    >
      <div
        className={`mx-auto max-w-7xl mt-3 px-4 md:px-6 transition-all duration-500 ${
          scrolled ? "py-2" : "py-3"
        }`}
      >
        <div
          className={`flex items-center justify-between px-4 md:px-6 py-3 rounded-2xl ${
            scrolled ? "glass" : "glass-soft"
          }`}
        >
          <button
            type="button"
            onClick={() => goto("top")}
            className="flex items-center gap-3 tap-target"
            data-testid="nav-logo"
            aria-label="Relyn — go to top"
          >
            <LogoMark size={44} />
            <span
              className="text-white text-xl md:text-[22px] tracking-tight"
              style={{ fontFamily: "Outfit", fontWeight: 600 }}
            >
              Relyn
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-7">
            {links.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => goto(l.id)}
                className="text-sm text-[#C0C7D1] hover:text-white transition-colors tap-target"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goto("waitlist", { focus: "input" })}
              className="btn-primary text-sm py-2.5 px-4 md:px-5"
              data-testid="nav-waitlist-btn"
            >
              Join Waitlist
            </button>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden h-11 w-11 rounded-full glass-soft border border-white/10 flex items-center justify-center text-white tap-target"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              data-testid="nav-mobile-toggle"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden fixed inset-0 top-0 bg-[#0A1A2F]/95 backdrop-blur-xl z-40 pt-24 px-6"
            data-testid="nav-mobile-menu"
            onClick={() => setMenuOpen(false)}
          >
            <motion.nav
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.35, delay: 0.05 }}
              className="flex flex-col gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  type="button"
                  onClick={() => goto(l.id)}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.3 }}
                  className="text-left text-white text-2xl font-light py-4 border-b border-white/[0.06] tap-target"
                  style={{ fontFamily: "Outfit" }}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                type="button"
                onClick={() => goto("waitlist", { focus: "input" })}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.35 }}
                className="mt-8 btn-primary w-full tap-target"
                data-testid="nav-mobile-waitlist-btn"
              >
                Join Waitlist
              </motion.button>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

export function LogoMark({ size = 32 }) {
  return (
    <span
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <span
        className="absolute inset-0 rounded-full"
        style={{
          boxShadow:
            "0 0 0 1px rgba(45,140,255,0.35), 0 0 24px rgba(45,140,255,0.35)",
        }}
      />
      <img
        src="/relyn-logo-88.png"
        alt="Relyn logo"
        width={size}
        height={size}
        loading="eager"
        decoding="async"
        className="relative rounded-full object-cover pointer-events-none"
        style={{ width: size, height: size }}
      />
    </span>
  );
}
